import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from 'urql';
import { useCallback, useEffect } from 'react';
import invariant from 'tiny-invariant';
import { union } from 'lodash';
import { DbIdsPerModel, useViewer } from '../ForgeViewer';
import { useViewManager } from '../ViewManagerContext';
import {
  GetInitialStateDocument,
  GetProjectSettingsDocument,
  SavedView,
} from 'src/gql/graphql';
import { useSavedViews } from 'src/hooks/saved-views';

type HandleInitialStateProps = {
  setCategories: (categories: string[]) => void;
  setSelectedLayers: (layers: string[]) => void;
  setOrderStatuses: (statuses: string[]) => void;
};

export default function HandleInitialState({
  setCategories,
  setSelectedLayers,
  setOrderStatuses,
}: HandleInitialStateProps) {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const initialStateId = searchParams.get('initial_state') || '';

  const [{ data: initialStateData }] = useQuery({
    query: GetInitialStateDocument,
    variables: { initialStateId },
  });

  const [{ data: getProjectSettings }] = useQuery({
    query: GetProjectSettingsDocument,
    variables: { projectId: projectId as string },
  });

  const { setCurrentItemIdSelection, setCurrentExpandedItemIds } =
    useViewManager();

  const globalOffset =
    getProjectSettings?.project?.settings?.viewerGlobalOffset ?? null;

  const { savedViews } = useSavedViews(projectId as string, false);

  const {
    setGhostMode,
    showLabels: { setIsShowingLabels },
    setOrthographicMode,
    viewer,
    loadedModels,
    visibilityManager: {
      hide: hideElements,
      hiddenElements,
      isolatedElements,
      isolate: isolateElements,
      reset: resetVisibility,
      hiddenModelUrns,
      setHiddenModelUrns: setHiddenModels,
    },
  } = useViewer();

  const loadView = useCallback(
    // eslint-disable-next-line complexity
    (savedView: SavedView) => {
      invariant(viewer, 'Viewer is not defined');
      invariant(globalOffset, 'Global offset is not defined');

      resetVisibility();

      if (savedView.cameraPosition) {
        const {
          positionX,
          positionY,
          positionZ,
          targetX,
          targetY,
          targetZ,
          upX,
          upY,
          upZ,
          aspect,
          fov,
          orthoScale,
          isPerspective,
          applyGlobalOffset,
        } = savedView.cameraPosition;

        viewer.setViewFromArray([
          positionX,
          positionY,
          positionZ,
          applyGlobalOffset ? targetX + globalOffset.x : targetX,
          applyGlobalOffset ? targetY + globalOffset.y : targetY,
          applyGlobalOffset ? targetZ + globalOffset.z : targetZ,
          upX,
          upY,
          upZ,
          aspect,
          fov,
          orthoScale,
          isPerspective ? 1 : 0,
        ]);
      }

      setCategories(savedView.categories);
      setSelectedLayers(savedView.layers);
      setOrderStatuses(savedView.statuses);

      if (savedView.hiddenModels) {
        setHiddenModels(savedView.hiddenModels);
      }

      let elementsToIsolate: DbIdsPerModel = {};
      for (const { modelUrn, dbIds } of savedView.isolatedElements ?? []) {
        elementsToIsolate[modelUrn] = union(
          dbIds,
          elementsToIsolate[modelUrn] ?? []
        );
      }
      isolateElements(elementsToIsolate);
      let elementsToHide: DbIdsPerModel = {};
      for (const { modelUrn, dbIds } of savedView.hiddenElements ?? []) {
        elementsToHide[modelUrn] = union(
          dbIds,
          // NOTE: We may not need union if we are sure that the arrays are always merged when saving here
          elementsToHide[modelUrn] ?? []
        );
      }
      hideElements(elementsToHide);
    },
    [
      viewer,
      globalOffset,
      resetVisibility,
      setCategories,
      setSelectedLayers,
      setOrderStatuses,
      isolateElements,
      hideElements,
      setHiddenModels,
    ]
  );

  useEffect(() => {
    const initialState = initialStateData?.initialState;

    if (initialState) {
      setGhostMode(initialState.viewSettings.ghosting || false);
      setIsShowingLabels(initialState.viewSettings.showLabels || false);
      setOrthographicMode(initialState.viewSettings.orthographicMode || false);

      const savedView = savedViews?.find(
        (view) => view.id === initialState.viewId
      );

      if (savedView) {
        loadView(savedView);
      }

      if (initialState.selectedOrderEntries) {
        setCurrentItemIdSelection(initialState.selectedOrderEntries);
      }

      if (initialState.expandedOrderEntries) {
        setCurrentExpandedItemIds(initialState.expandedOrderEntries);
      }
    }
  }, [
    initialStateData?.initialState,
    loadView,
    savedViews,
    setCurrentExpandedItemIds,
    setCurrentItemIdSelection,
    setGhostMode,
    setIsShowingLabels,
    setOrthographicMode,
  ]);

  return null;
}
